import { Injectable } from '@angular/core';
import { EventDoc } from '../models/event.model';
import { eventEndDate, eventStartDate, primaryLocation } from '../events/event-utils';

/**
 * "Add to calendar" for the events screens.
 *
 * iOS writes straight into the system calendar with EventKit. The web has no equivalent API, so the
 * portable substitute is an RFC 5545 `.ics` file handed to the browser as a download: iOS Safari
 * opens it in Calendar, Android Chrome offers Calendar as a handler, and desktop opens the default
 * calendar app. No permission prompt is involved, which also means none of iOS's
 * authorization-denied handling has an analogue here.
 *
 * TIMING. The entry is built from `eventDate.startDate` — the stored instant — not from the
 * `eventTime.startTime` string the cards display. That string is a bare "HH:mm" with no timezone, so
 * it cannot be resolved to an instant without the venue's zone, which the documents do not carry.
 * The two agree in practice: spot-checking production, where they differ it is by exactly the
 * venue's UTC offset, i.e. the timestamp rendered in venue-local time IS the displayed string. A
 * guest in the venue's timezone therefore sees the calendar entry at the wall-clock time the card
 * promised.
 */
@Injectable({ providedIn: 'root' })
export class EventCalendarService {

  /** iOS's fallback duration when an event declares no end. */
  private static readonly DEFAULT_DURATION_MS = 2 * 60 * 60 * 1000;

  /**
   * Download a one-event `.ics`. Returns false when the event has no start date and there is
   * nothing to schedule — the caller surfaces that rather than downloading an empty file.
   */
  save(event: EventDoc | null | undefined): boolean {
    if (!event) return false;

    const start = eventStartDate(event);
    if (!start) return false;

    const ics = this.buildIcs(event, start, this.resolveEnd(event, start));
    this.download(ics, this.fileName(event));
    return true;
  }

  /**
   * A multi-day event runs to its `endDate`; everything else gets the two-hour default.
   *
   * iOS always adds two hours, so a week-long festival lands in the calendar as a 2-hour block on
   * its first day. Honouring `endDate` where it exists (9 production documents) is a deliberate
   * divergence — the wrong duration is not something worth being bug-compatible about.
   */
  private resolveEnd(event: EventDoc, start: Date): Date {
    const end = eventEndDate(event);
    if (end && end.getTime() > start.getTime()) return end;
    return new Date(start.getTime() + EventCalendarService.DEFAULT_DURATION_MS);
  }

  private buildIcs(event: EventDoc, start: Date, end: Date): string {
    const location = primaryLocation(event);
    const locationText = [location?.address, location?.city || location?.place, location?.country]
      .map(part => (part ?? '').trim())
      .filter(Boolean)
      .join(', ');

    const lines = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Feather//Feather Menu//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      // The event id keeps re-downloads idempotent: a calendar that already holds this UID updates
      // the existing entry instead of creating a duplicate.
      `UID:${this.escape(event.id || String(start.getTime()))}@feather`,
      `DTSTAMP:${this.toIcsDate(new Date())}`,
      `DTSTART:${this.toIcsDate(start)}`,
      `DTEND:${this.toIcsDate(end)}`,
      `SUMMARY:${this.escape(event.title || '')}`,
    ];

    if (event.description) lines.push(`DESCRIPTION:${this.escape(event.description)}`);
    if (locationText) lines.push(`LOCATION:${this.escape(locationText)}`);
    if (event.eventWebUrl) lines.push(`URL:${this.escape(event.eventWebUrl)}`);

    lines.push('END:VEVENT', 'END:VCALENDAR');

    // CRLF is required by the spec, and some desktop clients reject bare LF outright.
    return lines.map(line => this.fold(line)).join('\r\n') + '\r\n';
  }

  /** UTC basic format — `20250422T190000Z`. */
  private toIcsDate(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return (
      String(date.getUTCFullYear()) +
      pad(date.getUTCMonth() + 1) +
      pad(date.getUTCDate()) + 'T' +
      pad(date.getUTCHours()) +
      pad(date.getUTCMinutes()) +
      pad(date.getUTCSeconds()) + 'Z'
    );
  }

  /** RFC 5545 §3.3.11 — backslash, semicolon and comma are delimiters; newlines become `\n`. */
  private escape(value: string): string {
    return value
      .replace(/\\/g, '\\\\')
      .replace(/;/g, '\\;')
      .replace(/,/g, '\\,')
      .replace(/\r\n|\r|\n/g, '\\n');
  }

  /**
   * RFC 5545 §3.1 content-line folding at 75 octets.
   *
   * Measured in UTF-8 bytes, not characters, and never split mid-codepoint — event titles are
   * routinely Cyrillic (2 bytes/char) and carry emoji (4 bytes), so a naive 75-character fold would
   * both under-fold and risk cutting a surrogate pair in half.
   */
  private fold(line: string): string {
    const encoder = new TextEncoder();
    if (encoder.encode(line).length <= 75) return line;

    const chunks: string[] = [];
    let current = '';
    let currentBytes = 0;
    // A continuation line is prefixed with a space, which itself costs one of the 75 octets.
    let limit = 75;

    for (const char of line) {
      const size = encoder.encode(char).length;
      if (currentBytes + size > limit) {
        chunks.push(current);
        current = '';
        currentBytes = 0;
        limit = 74;
      }
      current += char;
      currentBytes += size;
    }
    if (current) chunks.push(current);

    return chunks.join('\r\n ');
  }

  private fileName(event: EventDoc): string {
    const base = (event.title || 'event')
      .toLowerCase()
      .replace(/[^a-z0-9]+/gi, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60);
    // A title of only non-Latin characters (common: most titles here are Cyrillic) reduces to an
    // empty string above, so fall back rather than downloading a file called ".ics".
    return `${base || 'event'}.ics`;
  }

  private download(contents: string, fileName: string): void {
    const blob = new Blob([contents], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    // Must be in the document for the click to dispatch in Firefox.
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Revoking synchronously can cancel the download in Safari; one tick is enough.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}
